import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { trpc } from '@/lib/trpc';
import {
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatNumber, cn } from '@/lib/utils';
import { useCustomerLookup } from '@/hooks/useFKLookup';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { ComboboxFK } from '@/components/shared/ComboboxFK';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { exportToExcel } from '@/lib/excel.utils';
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const MILESTONES: [string, string][] = [
  ['in', 'In'], ['canMang', 'Cán màng'], ['keoLua', 'Kéo lụa'], ['epNhu', 'Ép nhũ'],
  ['be', 'Bế'], ['dan', 'Dán'], ['qc', 'QC'], ['dongGoi', 'Đóng gói'],
];
const milestoneText = (m: any) => {
  if (!m) return '';
  if (typeof m !== 'object') return String(m).trim() === '—' ? '' : String(m);
  if (!m.hasStep) return '';
  if (m.status === 'COMPLETED' || m.percent >= 100) return `${m.planQty ?? ''}${m.unit ?? ''} (Đã xong)`;
  return `${m.planQty ?? ''}${m.unit ?? ''} (${m.percent ?? 0}%) - ${m.formattedDeadline ?? ''}`;
};
const EXPORT_COLUMNS = [
  { key: 'orderId', label: 'Mã lệnh', width: 16 },
  { key: 'status', label: 'Trạng thái', width: 14 },
  { key: 'productName', label: 'Sản phẩm', width: 36 },
  { key: 'customer', label: 'Khách hàng', width: 20, exportFn: (po: any) => po.customer?.shortName || po.customer?.code || po.customer?.name || '' },
  { key: 'qtyOrder', label: 'SL đặt', width: 10, exportFn: (po: any) => po.qtyOrder || 0 },
  { key: 'qtyAllowance', label: 'Bù hao', width: 10, exportFn: (po: any) => po.qtyAllowance || 0 },
  { key: 'deadline', label: 'Hạn giao', width: 12, exportFn: (po: any) => (po.deadline ? new Date(po.deadline).toLocaleDateString('vi-VN') : '') },
  ...MILESTONES.map(([k, label]) => ({
    key: `ms_${k}`,
    label,
    width: 22,
    exportFn: (po: any) => milestoneText(((po.milestones as any) || (po.techSpecs as any) || {})[k]),
  })),
];

export function ProductionMilestoneMatrixPage() {
  const navigate = useNavigate();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = usePageSizeState(50);

  // Đọc từ khóa tìm kiếm đồng bộ từ Header search bar (?q=...)
  const { headerSearch } = useHeaderSearch();

  // Danh mục khách hàng động từ backend CSDL
  const { options: customerOptions } = useCustomerLookup();

  // Fetch Production Milestone Matrix đồng bộ backend CSDL
  const { data, isLoading } = useQuery(
    trpc.productionOrders.getMilestoneProgressMatrix.queryOptions({
      customerId: selectedCustomerId !== 'ALL' ? selectedCustomerId : undefined,
      search: headerSearch || undefined,
      page: currentPage,
      limit: pageSize,
    })
  );

  const items = data?.items || [];
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  // Render milestone cell with 2 lines management as requested:
  // Dòng 1: Số lượng kế hoạch (% HT) Ví dụ 1500sheet (50%)
  // Dòng 2: Thời hạn (tiến độ) --> 29/09 (còn 4 ngày)
  // Không có trong quy trình -> Đổi màu xám
  const renderMilestoneCell = (milestone: any) => {
    // 1. Trường hợp là object có cấu trúc từ backend
    if (milestone && typeof milestone === 'object') {
      if (!milestone.hasStep) {
        return (
          <div className="py-2 px-2 flex items-center justify-center min-h-[46px] h-full bg-slate-100/70 text-slate-300 font-mono text-xs select-none">
            —
          </div>
        );
      }

      const isCompleted = milestone.status === 'COMPLETED' || milestone.percent >= 100;
      const isRunning = milestone.status === 'RUNNING' || milestone.status === 'IN_PROGRESS';
      const isOverdue = milestone.isOverdue && !isCompleted;

      return (
        <div
          className={cn(
            'py-1 px-1.5 flex flex-col justify-center items-center text-center min-h-[46px] h-full transition-colors',
            isCompleted
              ? 'bg-emerald-50/60 hover:bg-emerald-100/70 text-emerald-950'
              : isRunning
              ? 'bg-blue-50/60 hover:bg-blue-100/70 text-blue-950'
              : isOverdue
              ? 'bg-rose-50/50 hover:bg-rose-100/60 text-rose-950'
              : 'bg-white hover:bg-slate-50 text-slate-900'
          )}
          title={`Trạng thái: ${milestone.status} | Đạt: ${formatNumber(milestone.actualQty || 0)} / ${formatNumber(milestone.planQty || 0)} ${milestone.unit} (${milestone.percent}%) | Hạn: ${milestone.formattedDeadline} (${milestone.dueText})`}
        >
          {/* Dòng 1: Số lượng kế hoạch (% HT) Ví dụ 1500sheet (50%) */}
          <div className="font-mono text-[11px] font-bold leading-tight whitespace-nowrap">
            {formatNumber(milestone.planQty)}{milestone.unit}{' '}
            <span
              className={cn(
                'text-[10px] font-semibold',
                isCompleted
                  ? 'text-emerald-700 font-bold'
                  : isRunning
                  ? 'text-blue-700 font-bold'
                  : isOverdue
                  ? 'text-rose-700 font-bold'
                  : 'text-slate-500'
              )}
            >
              ({milestone.percent}%)
            </span>
          </div>

          {/* Dòng 2: Thời hạn (tiến độ) --> 29/09 (còn 4 ngày) */}
          <div
            className={cn(
              'text-[10px] font-medium leading-tight mt-0.5 truncate max-w-[125px] whitespace-nowrap',
              isCompleted
                ? 'text-emerald-600 font-semibold'
                : isOverdue
                ? 'text-rose-600 font-semibold'
                : isRunning
                ? 'text-blue-600 font-semibold'
                : 'text-slate-500'
            )}
          >
            {isCompleted ? 'Đã xong' : `${milestone.formattedDeadline} (${milestone.dueText})`}
          </div>
        </div>
      );
    }

    // 2. Fallback nếu là string từ legacy
    if (!milestone || milestone === '—' || milestone === '-' || String(milestone).trim() === '') {
      return (
        <div className="py-2 px-2 flex items-center justify-center min-h-[46px] h-full bg-slate-100/70 text-slate-300 font-mono text-xs select-none">
          —
        </div>
      );
    }

    const clean = String(milestone).trim();
    return (
      <div className="py-2 px-1 text-center font-mono text-[11px] font-bold text-slate-800 min-h-[46px] flex items-center justify-center">
        {clean}
      </div>
    );
  };

  return (
    <ListPageShell
      onExport={() => exportToExcel(items, EXPORT_COLUMNS, 'ma_tran_tien_do_lsx', { sheetName: 'Ma trận tiến độ' })}
      unitLabel="lệnh sản xuất"
      totalItems={totalItems}
      currentPage={currentPage}
      pageSize={pageSize}
      onPageChange={setCurrentPage}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
      }}
      isLoading={isLoading}
      headerFilters={
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">Khách hàng:</span>
          <ComboboxFK
            value={selectedCustomerId === 'ALL' ? '' : selectedCustomerId}
            onChange={(id) => {
              setSelectedCustomerId(id || 'ALL');
              setCurrentPage(1);
            }}
            options={customerOptions}
            placeholder="Tất cả khách hàng"
            hideLabel
            className="w-[220px] sm:w-[260px] h-7 text-xs"
          />
          {selectedCustomerId !== 'ALL' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedCustomerId('ALL');
                setCurrentPage(1);
              }}
              className="h-7 text-xs px-2 text-slate-500 hover:text-slate-800"
            >
              Xóa lọc
            </Button>
          )}
        </div>
      }
      actions={
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            onClick={() => navigate({ to: '/production-orders' })}
            className="gap-1.5 text-xs h-7 bg-teal-600 hover:bg-teal-700 text-white"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            DS Lệnh SX
          </Button>
        </div>
      }
    >
      <table className="w-full text-left text-xs border-collapse min-w-[1250px]">
          <thead className="sticky top-0 z-30 bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 shadow-2xs">
            <tr className="divide-x divide-slate-200/60">
              {/* Sticky Col 1: Mã Lệnh */}
              <th className="py-2.5 px-3 w-[140px] min-w-[140px] max-w-[140px] text-slate-700 whitespace-nowrap sticky left-0 z-40 bg-slate-100 border-r border-slate-200">
                Mã Lệnh
              </th>
              {/* Sticky Col 2: Sản Phẩm & Mã Hàng */}
              <th className="py-2.5 px-3 w-[220px] min-w-[220px] max-w-[240px] text-slate-700 sticky left-[140px] z-40 bg-slate-100 border-r-2 border-slate-300 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                Sản Phẩm &amp; Mã Hàng
              </th>
              <th className="py-2.5 px-3 w-40 min-w-[150px] max-w-[200px] text-slate-700">Khách Hàng</th>
              <th className="py-2.5 px-3 w-28 text-right text-slate-700" title="Số lượng đặt (yêu cầu) và số lượng bù hao kế hoạch">SL Đặt &amp; Bù</th>
              <th className="py-2.5 px-3 w-24 text-right text-slate-700">Hạn Giao</th>
              {/* 8 Công đoạn trọng yếu (mỗi cột min-w 125px cho 2 dòng thông số) */}
              <th className="py-2 px-1 text-center bg-blue-50/70 text-blue-900 w-[125px] min-w-[125px]">In</th>
              <th className="py-2 px-1 text-center bg-indigo-50/70 text-indigo-900 w-[125px] min-w-[125px]">Cán Màng</th>
              <th className="py-2 px-1 text-center bg-purple-50/70 text-purple-900 w-[125px] min-w-[125px]">Kéo Lụa</th>
              <th className="py-2 px-1 text-center bg-pink-50/70 text-pink-900 w-[125px] min-w-[125px]">Ép Nhũ</th>
              <th className="py-2 px-1 text-center bg-amber-50/70 text-amber-900 w-[125px] min-w-[125px]">Bế</th>
              <th className="py-2 px-1 text-center bg-orange-50/70 text-orange-900 w-[125px] min-w-[125px]">Dán</th>
              <th className="py-2 px-1 text-center bg-cyan-50/70 text-cyan-900 w-[125px] min-w-[125px]">QC</th>
              <th className="py-2 px-1 text-center bg-emerald-50/70 text-emerald-900 w-[125px] min-w-[125px]">Đóng Gói</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={13} className="py-12 text-center text-slate-400">
                  Đang tải ma trận tiến độ sản xuất...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={13} className="py-12 text-center text-slate-400 italic">
                  Không tìm thấy dữ liệu tiến độ phù hợp
                </td>
              </tr>
            ) : (
              items.map((po: any) => {
                const ms = (po.milestones as any) || (po.techSpecs as any) || {};
                const isDone = po.status === 'COMPLETED';

                return (
                  <tr
                    key={po.id}
                    onClick={() => navigate({ to: '/production-orders/$orderId', params: { orderId: po.orderId } })}
                    className="hover:bg-slate-50/80 transition-colors cursor-pointer divide-x divide-slate-100 group"
                  >
                    {/* Sticky Col 1: Mã Lệnh không wraptext & Trạng thái lệnh ngay bên dưới */}
                    <td className="py-2 px-3 w-[140px] min-w-[140px] max-w-[140px] whitespace-nowrap align-top sticky left-0 z-20 bg-white group-hover:bg-slate-50 border-r border-slate-100 shadow-[1px_0_0_0_#f1f5f9]">
                      <div className="font-mono font-bold text-slate-900 leading-snug">
                        {po.orderId}
                      </div>
                      <div className="mt-1">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] font-semibold px-1.5 py-0 leading-tight',
                            isDone
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : po.status === 'RUNNING' || po.status === 'IN_PROGRESS'
                              ? 'bg-blue-100 text-blue-800 border-blue-300 animate-pulse'
                              : po.status === 'RELEASED'
                              ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                              : po.status === 'PLANNED'
                              ? 'bg-sky-100 text-sky-800 border-sky-300'
                              : 'bg-amber-100 text-amber-800 border-amber-300'
                          )}
                        >
                          {po.status === 'COMPLETED' ? 'Hoàn thành' :
                           po.status === 'RUNNING' || po.status === 'IN_PROGRESS' ? 'Đang chạy' :
                           po.status === 'RELEASED' ? 'Đã duyệt' :
                           po.status === 'PLANNED' ? 'Kế hoạch' :
                           po.status === 'DRAFT' ? 'Nháp' :
                           po.status === 'READY_FOR_SCHEDULING' ? 'Sẵn sàng' :
                           po.status || 'Chờ chạy'}
                        </Badge>
                      </div>
                    </td>

                    {/* Sticky Col 2: Sản Phẩm & Mã Hàng */}
                    <td className="py-2 px-3 w-[220px] min-w-[220px] max-w-[240px] align-top sticky left-[140px] z-20 bg-white group-hover:bg-slate-50 border-r-2 border-slate-200 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.04)]">
                      {/* Cho phép wrap text tối đa 2 dòng không bị cắt cụt */}
                      <div className="font-semibold text-slate-900 line-clamp-2 leading-snug" title={po.productName}>
                        {po.productName}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">
                        {po.productLine || po.poReference || ''}
                      </div>
                    </td>

                    <td className="py-2 px-3 text-slate-800 font-medium align-top">
                      {/* Hiển thị tên rút gọn ngắn gọn, có tooltip tên đầy đủ */}
                      <span className="truncate block" title={po.customer?.name || po.customer?.shortName}>
                        {po.customer?.shortName || po.customer?.code || po.customer?.name || '---'}
                      </span>
                    </td>

                    {/* Cột SL Đặt & Bù hao */}
                    <td className="py-2 px-3 text-right font-mono align-top whitespace-nowrap">
                      <div className="font-bold text-slate-900 text-xs">
                        {formatNumber(po.qtyOrder || 0)}
                      </div>
                      {(po.qtyAllowance > 0 || po.qtyPlan > 0) && (
                        <div className="text-[10px] text-slate-500 mt-0.5" title={`Bù hao: +${formatNumber(po.qtyAllowance || 0)} | Kế hoạch: ${formatNumber(po.qtyPlan || po.qtyOrder)}`}>
                          +bù: <span className="text-amber-700 font-semibold">{formatNumber(po.qtyAllowance || 0)}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-slate-500 align-top">
                      {po.deadline ? new Date(po.deadline).toLocaleDateString('vi-VN') : '---'}
                    </td>

                    {/* 8 Mốc công đoạn: Hiển thị đúng 2 dòng hoặc màu xám nếu không có trong quy trình */}
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.in)}</td>
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.canMang)}</td>
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.keoLua)}</td>
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.epNhu)}</td>
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.be)}</td>
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.dan)}</td>
                    <td className="p-0 text-center align-middle border-r border-slate-100">{renderMilestoneCell(ms.qc)}</td>
                    <td className="p-0 text-center align-middle">{renderMilestoneCell(ms.dongGoi)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
    </ListPageShell>
  );
}

export default ProductionMilestoneMatrixPage;
